import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  Backdrop,
  Box,
  Container,
  Divider,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import MenuNav from './components/MenuNav';
import MenuUser from './components/MenuUser';
import NavBrand from './components/NavBrand';
import UserProgress from './components/UserProgress';

// ------------------- styled components -------------------

const NavbarWrapper = styled(Box)(({ theme }) => ({
  zIndex: 5,
  width: '100%',
  height: '64px',
  position: 'fixed',
  transition: 'top 0.5s',
  backgroundColor: theme.palette.neutralWhite.main,
  boxShadow: '0px 4px 74px 0px rgba(0, 0, 0, 0.05)',

  [theme.breakpoints.down('md')]: {
    height: '72px',
    boxShadow: '0px 4px 12px 0px rgba(23, 23, 23, 0.08)',
  },
}));

const NavbarContent = styled(Stack)(({ theme }) => ({
  height: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: '40px',

  [theme.breakpoints.down('md')]: {
    paddingRight: '0px',
  },
}));

// ------------------- main components -------------------

const NavbarGeneral = ({ logOut, setOpenMenu }) => {
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));

  const { pathname } = useLocation();

  const { user } = useSelector((state) => state.auth);
  const { userProgress } = useSelector((state) => state.studentLearning);

  const [open, setOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const isSamePathname = useMemo(() => {
    const isOnlinePath = pathname.includes('/student-learning/video');
    const isPracticePath = pathname.includes('/student-learning/practice');
    const isWorkshopPath = pathname.includes('/student-learning/workshop');
    const isOnCampusPath = pathname.includes('/student-learning/on-campus');

    return { isOnlinePath, isPracticePath, isWorkshopPath, isOnCampusPath };
  }, [pathname]);

  const { isOnlinePath, isPracticePath } = isSamePathname;

  const handleCloseOverlay = () => {
    setOverlayOpen(!overlayOpen);
  };

  return (
    <>
      <Backdrop
        open={overlayOpen}
        sx={{ zIndex: 4 }}
        onClick={handleCloseOverlay}
      />

      <NavbarWrapper id="fixed-navbar">
        <Container maxWidth="xl" sx={{ height: '100%', px: '16px !important' }}>
          <NavbarContent>
            <Box sx={{ order: { xs: 1, md: 0 } }}>
              <NavBrand isSamePathname={isSamePathname} />
            </Box>

            <Stack direction="row" alignItems="center" spacing={3}>
              {(isOnlinePath || isPracticePath) && (
                <UserProgress title="class progress" progress={userProgress} />
              )}

              {!isMobile && (
                <>
                  <MenuNav isSamePathname={isSamePathname} />

                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{ color: 'neutral500.main' }}
                  />
                </>
              )}
              {/* renders user menu if user has logged in */}
              {Boolean(user.email) && (
                <MenuUser
                  user={user}
                  open={open}
                  logOut={logOut}
                  setOpen={setOpen}
                  setOverlay={setOverlayOpen}
                  sx={{ order: 0 }}
                />
              )}
            </Stack>

            {isMobile && (
              <Box
                component="img"
                src="static/icons/ic_menu.svg"
                sx={{ width: '24px', height: '24px', order: 3 }}
                onClick={() => setOpenMenu(true)}
              />
            )}
          </NavbarContent>
        </Container>
      </NavbarWrapper>
    </>
  );
};

export default NavbarGeneral;
